import { SealCheck } from "@phosphor-icons/react";
import { Panel, Pill } from "@/components/admin/AdminUI";
import { MediaTile } from "@/components/admin/ReviewMedia";
import { mediaUrl } from "@/lib/api";
import { formatDate, humanize } from "@/lib/format";

const ABOUT = [
  ["relationship_history", "Relationship history"], ["has_kids", "Has kids"], ["wants_kids", "Wants kids"],
  ["smoking", "Smoking"], ["drinking", "Drinking"], ["religion", "Religion"], ["relationship_goal", "Looking for"],
];
const VERIFY_TONE = { verified: "success", pending: "warning", rejected: "error", none: "neutral" };

const height = (cm) => {
  const inches = Math.round(cm / 2.54);
  return `${Math.floor(inches / 12)}′${inches % 12}″ (${cm} cm)`;
};

function Item({ label, children, testId }) {
  return (
    <div className="py-2.5" data-testid={testId}>
      <dt className="cw-muted text-xs">{label}</dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <section className="border-t border-border pt-5" data-testid={testId}>
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function dealbreakerText(key, v) {
  if (key === "religion") return v.mode === "same_as_mine" ? "Same as mine" : `Only: ${humanize(v.values)}`;
  return humanize(v);
}

function Later({ p }) {
  const rows = [
    p.height_cm && ["Height", height(p.height_cm)],
    p.occupation && ["Occupation", p.occupation],
    p.education && ["Education", humanize(p.education)],
    p.languages?.length && ["Languages", p.languages.join(", ")],
    p.ethnicity_private && ["Ethnicity", `${p.ethnicity_private}${p.show_ethnicity ? " (shown)" : " (hidden)"}`],
    p.currently_in && ["Currently in", p.currently_in],
  ].filter(Boolean);
  if (!rows.length) return null;
  return (
    <Section title="More about them" testId="profile-later-fields">
      <dl className="grid gap-x-8 sm:grid-cols-2 lg:grid-cols-3">{rows.map(([l, v]) => <Item key={l} label={l} testId={`profile-later-${l.toLowerCase().replace(/\s/g, "-")}`}>{v}</Item>)}</dl>
    </Section>
  );
}

export default function ProfilePanel({ data }) {
  const p = data.profile;
  const ob = data.onboarding || {};
  const v = data.verification || {};
  const status = v.status || "none";
  const photos = data.photos || [];
  const dealbreakers = Object.entries(p?.dealbreakers || {}).filter(([, x]) => x);

  return (
    <Panel className="space-y-5 p-6" data-testid="user-profile-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold" data-testid="profile-name-age">{p?.first_name || "No profile yet"}{p?.age ? `, ${p.age}` : ""}</h2>
          {p && <p className="cw-soft mt-1 text-sm" data-testid="profile-basics">{humanize(p.gender)} · looking for {humanize(p.looking_for).toLowerCase()} · {[p.home?.city || p.city, p.home?.state || p.state].filter(Boolean).join(", ") || "no city"}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill tone={VERIFY_TONE[status] || "neutral"} className="capitalize" data-testid="profile-verification-status">
            {status === "verified" && <SealCheck size={14} weight="fill" />}{status === "none" ? "Not verified" : status}
          </Pill>
          <Pill tone={ob.completed_at ? "success" : "brand"} data-testid="profile-onboarding-status">
            {ob.completed_at ? `Onboarding done ${formatDate(ob.completed_at, false)}` : `Onboarding: ${humanize(ob.current_step || "not started")}`}
          </Pill>
        </div>
      </div>

      <Section title={`Photos (${photos.length})`} testId="profile-photos">
        {photos.length || v.selfie_url ? (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {photos.map((ph, i) => <MediaTile key={ph.id} src={mediaUrl(ph.url)} alt={`Photo ${i + 1}`} label={ph.is_main ? "Main" : `#${i + 1}`} testId={`profile-photo-${i}`} />)}
            {v.selfie_url && <MediaTile src={mediaUrl(v.selfie_url)} alt="Verification selfie" label="Verification selfie" className="cw-ring-warning rounded-2xl" testId="profile-verification-selfie" />}
          </div>
        ) : <p className="cw-muted text-sm">No photos yet.</p>}
      </Section>

      {p && (
        <>
          <Section title="Prompts" testId="profile-prompts">
            {p.prompts?.length ? (
              <ol className="grid gap-3 md:grid-cols-3">
                {p.prompts.map((x, i) => (
                  <li key={x.prompt_id || i} className="rounded-2xl bg-[var(--cw-surface-tertiary)] p-4" data-testid={`profile-prompt-${i}`}>
                    <p className="cw-muted text-xs">{x.text}</p><p className="mt-1 text-sm">{x.answer}</p>
                  </li>
                ))}
              </ol>
            ) : <p className="cw-muted text-sm">No prompts answered yet.</p>}
          </Section>
          <Section title="About" testId="profile-about">
            <dl className="grid gap-x-8 sm:grid-cols-2 lg:grid-cols-4">{ABOUT.map(([k, l]) => <Item key={k} label={l} testId={`profile-about-${k}`}>{humanize(p.about?.[k])}</Item>)}</dl>
          </Section>
          <Section title="Hard no's" testId="profile-dealbreakers">
            {dealbreakers.length ? (
              <ul className="flex flex-wrap gap-2">{dealbreakers.map(([k, x]) => <li key={k}><Pill tone="neutral" className="py-1">{humanize(k)}: {dealbreakerText(k, x)}</Pill></li>)}</ul>
            ) : <p className="cw-muted text-sm">None set.</p>}
          </Section>
          <Section title="Bio" testId="profile-bio"><p className="cw-soft whitespace-pre-line text-sm leading-6">{p.bio || "—"}</p></Section>
          <Section title="Preferences" testId="profile-preferences">
            <dl className="grid gap-x-8 sm:grid-cols-3">
              <Item label="Age range">{p.preferences?.age_min}–{p.preferences?.age_max}</Item>
              <Item label="Distance">{p.preferences?.distance_miles} miles</Item>
              <Item label="Home ZIP">{p.home?.zip || "—"}</Item>
            </dl>
          </Section>
          <Later p={p} />
        </>
      )}
    </Panel>
  );
}
